// Season editor (New Season): pick a season (Pre season = template, Active,
// Archive), edit it, "Save changes" -> PUT v2/seasons/... (one PUT per season in
// bulk edit, writing only the fields the user changed, and each season's
// sensors as the sensors section left them).
// Pre season lists every block: its pre-season, or a copy of its current
// season that "Save changes" creates (POST v2/seasons/.../pre_season). The
// header's ⋮ "Activate now" makes a saved pre-season the block's active season
// (POST .../activate: V1 create, the previous active season archived).
const React = require('react');
const { useState, useEffect, useMemo, useCallback, useRef } = require('react');
const { useDispatch, useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const {
  save_new_season_season,
  load_new_season_season_detail,
  create_new_season_pre_season,
  activate_new_season_pre_season,
} = require('@actions/newSeasonActions');
const fieldManager = require('fieldManager');
const { convert } = require('unitsManager');
const {
  findV2Distribution,
  findV2FarmingUnit,
  sortByName,
} = require('./v2Distributions');
const helpers = require('./seasonHelpers');
const { getBlockAreas } = require('./layoutHelpers');
const {
  buildSeasonSaveBody,
  buildActivationBody,
  parseSeasonField,
  withChanges,
} = require('./seasonSaveBody');
const GeneralClasses = require('GeneralClasses');
const { parseFarmingUnitSensors } = require('./sensorEditRules');
const { useSeasonSensors, sensorsOfDetail } = require('./useSeasonSensors');
const {
  farmingUnitEditorUrl,
  seasonEditorUrl,
} = require('./newSeasonConstants');
const { useToast, Toast, SegmentedTabs, Modal } = require('./NewSeasonUi');
const BlocksMap = require('./BlocksMap');
const SeasonList = require('./SeasonList');
const SensorsSection = require('./SensorsSection');
const {
  Section,
  GeneralSection,
  DatesSection,
  IrrigationSection,
  ContractSection,
} = require('./SeasonForm');
const {
  SeasonAdvanceSection,
  useAdvanceSection,
} = require('./SeasonAdvanceSection');

const { SEASON_TABS } = helpers;

// Distribution / farming-unit selects come from GET v2/distributions only.
const useDistributions = () => {
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  return useMemo(() => sortByName(distributions), [distributions]);
};

const EntitySelects = ({
  distributions,
  distributionId,
  farmingUnitId,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const distribution = findV2Distribution(distributions, distributionId);
  const farmingUnits = sortByName(distribution?.farming_units);
  const onDistribution = (id) => {
    const next = findV2Distribution(distributions, id);
    const firstFarmingUnit = sortByName(next?.farming_units)[0];
    if (firstFarmingUnit) {
      onNavigate(seasonEditorUrl(id, firstFarmingUnit.id));
    }
  };
  return (
    <div className='ns-entity-selects'>
      <div className='ns-select-entity'>
        <div className='ns-select-entity-title'>{t('distribution')}</div>
        <select
          name={'ns-select-entity-dist'}
          className='form-control ns-input custom-select'
          value={distributionId}
          aria-label={t('distribution')}
          onChange={(e) => onDistribution(e.target.value)}
        >
          {!distribution && (
            <option value={distributionId}>{distributionId}</option>
          )}
          {distributions.map((current) => (
            <option key={current.id} value={current.id}>
              {current.name}
            </option>
          ))}
        </select>
      </div>

      <div className='ns-select-entity'>
        <div className='ns-select-entity-title'>{t('farming_unit')}</div>
        <select
          name={'ns-select-entity-fu'}
          className='form-control custom-select ns-input'
          value={farmingUnitId}
          aria-label={t('farming_unit')}
          onChange={(e) =>
            onNavigate(seasonEditorUrl(distributionId, e.target.value))
          }
        >
          {!farmingUnits.some((current) => current.id === farmingUnitId) && (
            <option value={farmingUnitId}>{farmingUnitId}</option>
          )}
          {farmingUnits.map((current) => (
            <option key={current.id} value={current.id}>
              {current.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Selection: a single selected row, or the checked rows in bulk mode.
// `nextKeyRef`: the row to select once the tab changes (after "Activate now").
const useSeasonSelection = (rows, tab, nextKeyRef) => {
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState([]);
  useEffect(() => {
    setSelectedKey(nextKeyRef.current);
    nextKeyRef.current = null;
    setCheckedKeys([]);
  }, [tab, bulkMode, nextKeyRef]);
  const rowByKey = useMemo(
    () => new Map(rows.map((row) => [row.key, row])),
    [rows],
  );
  const effectiveKey = rowByKey.has(selectedKey) ? selectedKey : rows[0]?.key;
  const activeRows = bulkMode
    ? checkedKeys.map((key) => rowByKey.get(key)).filter(Boolean)
    : [rowByKey.get(effectiveKey)].filter(Boolean);
  const toggle = useCallback(
    (key) =>
      setCheckedKeys((previous) =>
        previous.includes(key)
          ? previous.filter((current) => current !== key)
          : previous.concat([key]),
      ),
    [],
  );
  const toggleAll = () =>
    setCheckedKeys((previous) =>
      previous.length === rows.length ? [] : rows.map((row) => row.key),
    );
  return {
    bulkMode,
    setBulkMode,
    selectedKey: effectiveKey,
    setSelectedKey,
    checkedKeys,
    toggle,
    toggleAll,
    activeRows,
  };
};

// Same array instance while the active seasons are the same objects, so the
// form only resets when the selection changes or a season is re-saved.
const useStableSeasons = (activeRows) => {
  const previousRef = useRef([]);
  const seasons = activeRows.map((row) => row.season);
  const previous = previousRef.current;
  const isSame =
    previous.length === seasons.length &&
    seasons.every((season, index) => season === previous[index]);
  if (!isSame) {
    previousRef.current = seasons;
  }
  return previousRef.current;
};

// Form values for the active seasons, and the Advance section's Field path
// edits ({path: value}); both reset whenever the active seasons change.
// `changes()`: everything to save, form keys first (so an explicit
// recommendation display follows an irrigation type change).
const useSeasonForm = (activeRows) => {
  const seasons = useStableSeasons(activeRows);
  const initialValues = useMemo(() => helpers.commonValues(seasons), [seasons]);
  const [values, setValues] = useState(initialValues);
  const [fieldEdits, setFieldEdits] = useState({});
  useEffect(() => {
    setValues(initialValues);
    setFieldEdits({});
  }, [initialValues]);
  const changed = helpers.changedKeys(initialValues, values);
  const onChange = useCallback(
    (key, value) => setValues((previous) => ({ ...previous, [key]: value })),
    [],
  );
  const onChangeMany = useCallback(
    (patch) => setValues((previous) => ({ ...previous, ...patch })),
    [],
  );
  const onFieldEdit = useCallback(
    (path, value) =>
      setFieldEdits((previous) => ({ ...previous, [path]: value })),
    [],
  );
  const isChanged = changed.length > 0 || Object.keys(fieldEdits).length > 0;
  const changes = () => ({ ...helpers.pick(values, changed), ...fieldEdits });
  return {
    values,
    changed,
    fieldEdits,
    isChanged,
    changes,
    onChange,
    onChangeMany,
    onFieldEdit,
  };
};

// GET v2/seasons/... for every active season:
// {[seasonId]: {season, farmingUnitSensors} | {error}}. The detail is the only
// source of a season's sensors (the tree has none) and of the farming unit's
// sensors that a Tal Gil sensor links to.
const useSeasonDetails = (distributionId, farmingUnitId, activeRows) => {
  const dispatch = useDispatch();
  const [details, setDetails] = useState({});
  // A pre-season not created yet has no detail to fetch.
  const seasonIds = activeRows
    .filter((row) => !row.isNewPreSeason)
    .map((row) => row.season.id);
  const missingKey = seasonIds.filter((id) => !details[id]).join('|');
  const fetchDetail = useCallback(
    (seasonId) =>
      dispatch(
        load_new_season_season_detail(distributionId, farmingUnitId, seasonId),
      ).then((detail) => ({
        season: detail.season,
        farmingUnitSensors: detail.farming_unit_sensors,
      })),
    [dispatch, distributionId, farmingUnitId],
  );
  const setDetail = useCallback(
    (seasonId, entry) =>
      setDetails((previous) => ({ ...previous, [seasonId]: entry })),
    [],
  );
  useEffect(() => {
    missingKey
      .split('|')
      .filter(Boolean)
      .forEach((seasonId) =>
        fetchDetail(seasonId).then(
          (detail) => setDetail(seasonId, detail),
          (error) => setDetail(seasonId, { error: String(error) }),
        ),
      );
  }, [missingKey, fetchDetail, setDetail]);
  return { details, fetchDetail, setDetail };
};

// The V2 farming unit and its distribution's name: what V1 picks the default
// week start from (seasonSaveBody.firstDayOfWeekFor).
const useSeasonEntity = (distributionId, farmingUnitId) => {
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  return useMemo(
    () => ({
      farmingUnit: findV2FarmingUnit(
        distributions,
        distributionId,
        farmingUnitId,
      ),
      distributionName: findV2Distribution(distributions, distributionId)?.name,
    }),
    [distributions, distributionId, farmingUnitId],
  );
};

// Edit Field's required inputs for the selected seasons. Active / archived
// seasons must pass them to be saved; a pre-season may be saved partial and
// must pass them to be activated. The sow and pre-season irrigation start
// dates must be before every selected season's harvest date, for every save
// (pre-seasons too) and for activation; the error names the harvest date.
const useSeasonValidation = (activeRows, form) => {
  const values = form.values;
  const lengthUnit = useSelector(
    (state) => state.authentication_data?.user_data?.length,
  );
  const allErrors = {
    ...helpers.validateSeasonValues(values, { lengthUnit }),
    ...helpers.areaSizeErrors(values, form.changed),
  };
  // Every selected season's harvest date must be after its sow and pre-season
  // irrigation start dates (the form's, else each season's own).
  const harvestErrors = helpers.harvestErrorsOf(
    activeRows.map((row) => row.season),
    values,
  );
  Object.keys(harvestErrors).forEach((key) => {
    if (!allErrors[key]) {
      allErrors[key] = harvestErrors[key];
    }
  });
  // A pre-season may be saved incomplete, but never with harvest first.
  const isTemplateOnly = activeRows.every(
    (row) => row.season.state === 'template',
  );
  const errors = isTemplateOnly ? harvestErrors : allErrors;
  const errorsOfSeason = (season) =>
    helpers.validateSeasonValues(helpers.commonValues([season]), {
      lengthUnit,
      harvestDate: season?.crop_data?.harvest_date,
    });
  return { errors, errorsOfSeason };
};

const saveSequentially = (rows, save) =>
  rows.reduce(
    (chain, row) =>
      chain.then((result) =>
        Promise.resolve(save(row))
          .then(() => ({ ...result, saved: result.saved + 1 }))
          .catch((error) => ({
            ...result,
            failed: result.failed.concat([row.block.name + ': ' + error]),
          })),
      ),
    Promise.resolve({ saved: 0, failed: [] }),
  );

// The rows "Save changes" writes: every active row when the form changed,
// otherwise only the seasons whose sensors changed — and always a pre-season
// that is not created yet (saving it as copied creates it).
const rowsToSave = (activeRows, form, seasonSensors) =>
  form.isChanged
    ? activeRows
    : activeRows.filter(
        (row) =>
          row.isNewPreSeason ||
          seasonSensors.editedSeasonIds.includes(row.season.id),
      );

// POST v2/seasons/.../pre_season: the block's pre-season, a copy of its
// current season with the changed values on top (sent as a template body).
const createPreSeason = (dispatch, ids, row, changes) =>
  Promise.resolve(
    dispatch(
      create_new_season_pre_season(ids.distributionId, ids.farmingUnitId, {
        block_id: row.block.id,
        ...helpers.buildTemplateBody(
          row.season,
          changes,
          fieldManager.defaultRecommendationDisplay,
        ),
      }),
    ),
  );

const useSaveSeasons = ({
  distributionId,
  farmingUnitId,
  activeRows,
  form,
  seasonDetails,
  seasonSensors,
  blockingReason,
  validation,
  checkAdvance,
  reloadTree,
  showToast,
  t,
}) => {
  const dispatch = useDispatch();
  const entity = useSeasonEntity(distributionId, farmingUnitId);
  const [isSaving, setIsSaving] = useState(false);
  // Fresh detail right before each PUT. A season sends the sensors the user
  // left it with, or — untouched — its current ones as Edit Field would
  // send them; the PUT response (with sensors) refreshes the detail.
  const saveRow = (row, changes) =>
    row.isNewPreSeason
      ? createPreSeason(
          dispatch,
          { distributionId, farmingUnitId },
          row,
          changes,
        )
      : saveExistingRow(row, changes);
  const saveExistingRow = (row, changes) =>
    seasonDetails.fetchDetail(row.season.id).then((detail) => {
      const sensors =
        seasonSensors.editedSensorsOf(row.season.id) || sensorsOfDetail(detail);
      let body;
      try {
        body = buildSeasonSaveBody(detail.season, changes, entity, sensors);
      } catch (error) {
        return Promise.reject(t('new_season_sensors_missing'));
      }
      return Promise.resolve(
        dispatch(
          save_new_season_season(
            distributionId,
            farmingUnitId,
            row.season.id,
            body,
          ),
        ),
      ).then((saved) => {
        if (saved) {
          seasonDetails.setDetail(row.season.id, {
            season: saved,
            farmingUnitSensors: detail.farmingUnitSensors,
          });
        }
        seasonSensors.clear(row.season.id);
      });
    });
  const save = () => {
    // Edit Field's form checks the advanced inputs too.
    const isAdvanceValid = checkAdvance();
    if (Object.keys(validation.errors).length > 0 || !isAdvanceValid) {
      showToast(t('new_season_fix_errors'), 'error');
      return;
    }
    if (blockingReason) {
      showToast(blockingReason, 'error');
      return;
    }
    const rows = rowsToSave(activeRows, form, seasonSensors);
    if (rows.length === 0) {
      showToast(t('new_season_nothing_changed'), 'error');
      return;
    }
    const changes = form.changes();
    setIsSaving(true);
    saveSequentially(rows, (row) => saveRow(row, changes)).then(
      ({ saved, failed }) => {
        setIsSaving(false);
        // Created pre-seasons replace their copies once the tree is re-read.
        if (rows.some((row) => row.isNewPreSeason)) {
          reloadTree();
        }
        if (failed.length === 0) {
          showToast(
            rows.length > 1
              ? t('new_season_bulk_saved', { count: saved })
              : t('new_season_changes_saved'),
          );
        } else {
          showToast(
            t('new_season_bulk_partial', {
              saved,
              total: rows.length,
              failed: failed.join('; '),
            }),
            'error',
          );
        }
      },
    );
  };
  return { isSaving, save };
};

// The block's current active season, which "Activate now" archives.
const activeSeasonOf = (block) =>
  (block?.seasons || []).find((season) => season.state === 'active') || null;

// "Activate now": confirm, then build V1's create body from the pre-season's
// fresh detail and POST .../activate (which re-reads the tree). Unsaved edits
// must be saved first, so the activated season is what the user saw saved.
// A block whose pre-season is still the unsaved copy gets it created first
// (as "Save changes" would), then activated.
const useActivatePreSeason = ({
  distributionId,
  farmingUnitId,
  seasonDetails,
  errorsOfSeason,
  reloadTree,
  onActivated,
  showToast,
  t,
}) => {
  const dispatch = useDispatch();
  const entity = useSeasonEntity(distributionId, farmingUnitId);
  const [pendingRow, setPendingRow] = useState(null);
  const [isActivating, setIsActivating] = useState(false);
  const request = (row, isDirty) => {
    if (isDirty) {
      showToast(t('new_season_activate_save_first'), 'error');
      return;
    }
    // V1's create needs what Edit Field requires.
    const errors = errorsOfSeason(row.season);
    if (errors.sow_date?.id) {
      showToast(t(errors.sow_date.id, errors.sow_date.params), 'error');
      return;
    }
    if (Object.keys(errors).length > 0) {
      showToast(t('new_season_activate_incomplete'), 'error');
      return;
    }
    setPendingRow(row);
  };
  const cancel = useCallback(() => setPendingRow(null), []);
  // The saved pre-season's id: the row's, or a pre-season created now.
  const savedPreSeasonId = (row) =>
    row.isNewPreSeason
      ? createPreSeason(
          dispatch,
          { distributionId, farmingUnitId },
          row,
          {},
        ).then((season) => season.id)
      : Promise.resolve(row.season.id);
  const activate = (seasonId) =>
    seasonDetails
      .fetchDetail(seasonId)
      .then((detail) =>
        dispatch(
          activate_new_season_pre_season(
            distributionId,
            farmingUnitId,
            seasonId,
            buildActivationBody(
              detail.season,
              entity,
              Math.floor(Date.now() / 1000),
            ),
          ),
        ),
      );
  const confirm = () => {
    const row = pendingRow;
    let createdId = null;
    setIsActivating(true);
    savedPreSeasonId(row)
      .then((seasonId) => {
        createdId = row.isNewPreSeason ? seasonId : null;
        return activate(seasonId).then(() => seasonId);
      })
      .then((seasonId) => {
        showToast(t('new_season_activated', { name: row.block.name }));
        onActivated(seasonId);
      })
      .catch((error) => {
        // Created but not activated: show the saved pre-season.
        if (createdId) {
          reloadTree();
        }
        showToast(String(error), 'error');
      })
      .finally(() => {
        setIsActivating(false);
        setPendingRow(null);
      });
  };
  return { pendingRow, isActivating, request, cancel, confirm };
};

// "Block 1 / Sep 2025" (or just the block) for the season being archived.
const archivedSeasonLabel = (block, season) => {
  const month = helpers.formatSeasonMonth(season.crop_data?.sow_date);
  return month ? `${block.name} / ${month}` : block.name;
};

const ActivateModal = ({ activation, tab }) => {
  const { t } = useTranslation();
  const row = activation.pendingRow;
  const current = activeSeasonOf(row?.block);
  return (
    <Modal
      isOpen={Boolean(row)}
      onClose={activation.isActivating ? () => {} : activation.cancel}
      className='ns-modal-small'
    >
      <div className='ns-modal-header'>
        {t('new_season_activate_title', {
          name: row ? helpers.seasonTitle(row, tab) : '',
        })}
      </div>
      <div className='ns-modal-body ns-modal-text'>
        {current
          ? t('new_season_activate_text_archive', {
              season: archivedSeasonLabel(row.block, current),
            })
          : t('new_season_activate_text_no_active')}{' '}
        {t('new_season_activate_text_sensors')}
      </div>
      <div className='ns-modal-footer'>
        <button
          type='button'
          className='ns-btn ns-btn-outline'
          disabled={activation.isActivating}
          onClick={activation.cancel}
        >
          {t('cancel')}
        </button>
        <button
          type='button'
          className='ns-btn ns-btn-primary'
          disabled={activation.isActivating}
          onClick={activation.confirm}
        >
          {t('new_season_activate_now')}
          {activation.isActivating && (
            <span
              className='spinner-border spinner-border-sm ml-2'
              role='status'
            />
          )}
        </button>
      </div>
    </Modal>
  );
};

// The season header's ⋮ menu (Pre season tab, one saved pre-season).
const SeasonMenu = ({ onActivate }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className='ns-season-menu'>
      <button
        type='button'
        className='ns-season-menu-btn'
        title={t('new_season_season_actions')}
        aria-label={t('new_season_season_actions')}
        onClick={() => setIsOpen((open) => !open)}
      >
        ⋮
      </button>
      {isOpen && (
        <div
          className='ns-season-menu-list'
          onMouseLeave={() => setIsOpen(false)}
        >
          <button
            type='button'
            onClick={() => {
              setIsOpen(false);
              onActivate();
            }}
          >
            {t('new_season_activate_now')}
          </button>
        </div>
      )}
    </div>
  );
};

// The farming unit's sensors (the same in every season's detail).
const useFarmingUnitSensors = (details) => {
  const json = Object.values(details).find(
    (detail) => detail?.farmingUnitSensors,
  )?.farmingUnitSensors;
  return useMemo(() => parseFarmingUnitSensors(json, GeneralClasses), [json]);
};

const useSensorsProps = ({
  distributions,
  distributionId,
  farmingUnitId,
  details,
  seasonSensors,
  onBlockingChange,
}) => {
  const farmingUnitSensors = useFarmingUnitSensors(details);
  return {
    sensorsBySeason: seasonSensors.sensorsBySeason,
    farmingUnitSensors,
    onUpdateSensors: seasonSensors.update,
    onBlockingChange,
    farmingUnitName: findV2FarmingUnit(
      distributions,
      distributionId,
      farmingUnitId,
    )?.name,
    distributionName: findV2Distribution(distributions, distributionId)?.name,
  };
};

// The selected season parsed as V1 parses a field, with the form's changes
// applied, for the Advance section: one created season at a time, once its
// detail is loaded. `unavailableMessage` says why there is none.
const useAdvanceField = ({ singleRow, details, entity, form }) => {
  const { t } = useTranslation();
  const detailSeason = singleRow ? details[singleRow.season.id]?.season : null;
  // Re-parsed only for a new detail (a selection change or a save).
  const base = useMemo(
    () => (detailSeason ? parseSeasonField(detailSeason, entity) : null),
    [detailSeason, entity],
  );
  const { values, fieldEdits } = form;
  const field = useMemo(
    () => (base ? withChanges(base, form.changes()) : null),
    [base, values, fieldEdits], // eslint-disable-line react-hooks/exhaustive-deps
  );
  if (field) {
    return { field, unavailableMessage: null };
  }
  if (!singleRow) {
    return { field: null, unavailableMessage: t('new_season_advance_bulk') };
  }
  return {
    field: null,
    unavailableMessage: singleRow.isNewPreSeason
      ? t('new_season_advance_save_first')
      : t('new_season_advance_loading'),
  };
};

// The selected seasons' contract installation date (MULTIPLE_VALUES when they
// differ).
const installationDateOf = (activeRows) => {
  const dates = activeRows.map(
    (row) => row.season.contract_data?.installation_date ?? null,
  );
  return dates.every((date) => date === dates[0])
    ? dates[0]
    : helpers.MULTIPLE_VALUES;
};

// UpdateFieldModal.calculateArea: the polygon's area in the user's unit.
const usePolygonArea = (activeRows) => {
  const areaUnit = useSelector(
    (state) => state.authentication_data?.user_data?.area,
  );
  const areas =
    activeRows.length === 1 ? activeRows[0].season.geo_data?.areas : null;
  return useMemo(
    () =>
      areas?.length
        ? convert(fieldManager.getFieldArea(areas), 'dunam', areaUnit, 'area')
        : null,
    [areas, areaUnit],
  );
};

// UpdateFieldModal: the latest measured irrigation rate, when the season has
// one.
const measuredRateOf = (season) => {
  const values = season?.historical_data?.measured_irrigation_rate?.values;
  return values ? Object.values(values)[0] : null;
};

const useMapBlocks = (tree) =>
  useMemo(
    () =>
      (tree.field_groups || []).reduce(
        (blocks, fieldGroup) =>
          blocks.concat(
            (fieldGroup.blocks || []).map((block) => ({
              key: block.id,
              name: block.name,
              areas: getBlockAreas(block),
            })),
          ),
        [],
      ),
    [tree],
  );

const SeasonFormPanel = ({
  tab,
  activeRows,
  onActivate,
  form,
  errors,
  mapBlocks,
  onMapBlockClick,
  isSaving,
  onSave,
  seasonDetails,
  sensorsProps,
  fitKey,
  isModerator,
  advance,
  advanceField,
  onAdvanceEditingChange,
}) => {
  const { t } = useTranslation();
  const selectedBlockKeys = useMemo(
    () => activeRows.map((row) => row.block.id),
    [activeRows],
  );
  const polygonArea = usePolygonArea(activeRows);
  if (activeRows.length === 0) {
    return (
      <div className='ns-card ns-right-card ns-center-message'>
        {t('new_season_select_season')}
      </div>
    );
  }
  const isTemplate = activeRows.every((row) => row.season.state === 'template');
  return (
    <div className='ns-card ns-right-card ns-season-form'>
      <div className='ns-title-bar ns-title-static'>
        {helpers.seasonTitle(activeRows[0], tab)}
        {onActivate && <SeasonMenu onActivate={onActivate} />}
      </div>
      <div className='ns-form-scroll'>
        <Section
          title={t('new_season_section_map')}
          className={' d-flex flex-column'}
        >
          <BlocksMap
            className='ns-map-medium'
            blocks={mapBlocks}
            selectedKeys={selectedBlockKeys}
            focusKeys={selectedBlockKeys}
            fitKey={fitKey}
            onBlockClick={onMapBlockClick}
          />
        </Section>
        <GeneralSection
          values={form.values}
          errors={errors}
          onChange={form.onChange}
          onChangeMany={form.onChangeMany}
          isModerator={isModerator}
          polygonArea={polygonArea}
        />
        <DatesSection
          values={form.values}
          errors={errors}
          onChange={form.onChange}
          isModerator={isModerator}
        />
        <IrrigationSection
          values={form.values}
          errors={errors}
          onChange={form.onChange}
          onChangeMany={form.onChangeMany}
          isModerator={isModerator}
          measuredRate={
            activeRows.length === 1
              ? measuredRateOf(
                  seasonDetails.details[activeRows[0].season.id]?.season,
                )
              : null
          }
        />
        {!isTemplate && (
          <SensorsSection
            {...sensorsProps}
            rows={activeRows}
            details={seasonDetails.details}
            mapBlocks={mapBlocks}
            selectedBlockKeys={selectedBlockKeys}
          />
        )}
        <ContractSection
          values={form.values}
          onChange={form.onChange}
          installationDate={installationDateOf(activeRows)}
          isModerator={isModerator}
        />
        <SeasonAdvanceSection
          advance={advance}
          field={advanceField.field}
          unavailableMessage={advanceField.unavailableMessage}
          isModerator={isModerator}
          farmingUnitSensors={sensorsProps.farmingUnitSensors}
          onFieldEdit={form.onFieldEdit}
          onCropDetailsChange={(cropType, variety, feature) =>
            form.onChangeMany({
              crop_type: cropType,
              crop_variety: variety,
              crop_feature: feature,
            })
          }
          onEditingChange={onAdvanceEditingChange}
        />
      </div>
      <div className='ns-sticky-footer'>
        <button
          type='button'
          className='ns-btn ns-btn-primary'
          disabled={isSaving}
          onClick={onSave}
        >
          {t('new_season_save_changes')}
          {isSaving && (
            <span
              className='spinner-border spinner-border-sm ml-2'
              role='status'
            />
          )}
        </button>
      </div>
    </div>
  );
};

const SeasonEditor = ({
  distributionId,
  farmingUnitId,
  tree,
  reloadTree,
  setDirty,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const distributions = useDistributions();
  const [tab, setTab] = useState(SEASON_TABS.ACTIVE);
  const nextKeyRef = useRef(null);
  const groups = useMemo(
    () => helpers.buildSeasonGroups(tree, tab),
    [tree, tab],
  );
  const rows = useMemo(() => helpers.flattenRows(groups), [groups]);
  const selection = useSeasonSelection(rows, tab, nextKeyRef);
  const form = useSeasonForm(selection.activeRows);
  const { toast, showToast } = useToast();
  const seasonDetails = useSeasonDetails(
    distributionId,
    farmingUnitId,
    selection.activeRows,
  );
  const seasonSensors = useSeasonSensors(
    selection.activeRows,
    seasonDetails.details,
  );
  const [blockingReason, setBlockingReason] = useState(null);
  // Edit Field blocks saving while a planner timeframe is mid-edit.
  const [isAdvanceEditing, setIsAdvanceEditing] = useState(false);
  const advance = useAdvanceSection();
  const isDirty = form.isChanged || seasonSensors.isDirty;
  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);
  const validation = useSeasonValidation(selection.activeRows, form);
  const isTemplateOnly = selection.activeRows.every(
    (row) => row.season.state === 'template',
  );
  const { isSaving, save } = useSaveSeasons({
    distributionId,
    farmingUnitId,
    activeRows: selection.activeRows,
    form,
    seasonDetails,
    seasonSensors,
    blockingReason:
      blockingReason ||
      (isAdvanceEditing ? t('edit_field_finish_open_edit_message') : null),
    validation,
    // A pre-season may be saved partial, as with the other inputs.
    checkAdvance: () => isTemplateOnly || advance.check(),
    reloadTree,
    showToast,
    t,
  });
  // After "Activate now": the Active tab, with the new season selected.
  const activation = useActivatePreSeason({
    distributionId,
    farmingUnitId,
    seasonDetails,
    errorsOfSeason: validation.errorsOfSeason,
    reloadTree,
    onActivated: (seasonId) => {
      nextKeyRef.current = seasonId;
      setTab(SEASON_TABS.ACTIVE);
    },
    showToast,
    t,
  });
  const singleRow =
    !selection.bulkMode && selection.activeRows.length === 1
      ? selection.activeRows[0]
      : null;
  const canActivate = tab === SEASON_TABS.PRE_SEASON && Boolean(singleRow);
  const entity = useSeasonEntity(distributionId, farmingUnitId);
  const advanceField = useAdvanceField({
    singleRow,
    details: seasonDetails.details,
    entity,
    form,
  });
  const sensorsProps = useSensorsProps({
    distributions,
    distributionId,
    farmingUnitId,
    details: seasonDetails.details,
    seasonSensors,
    onBlockingChange: setBlockingReason,
  });
  const mapBlocks = useMapBlocks(tree);

  const onMapBlockClick = (blockId) => {
    const row = rows.find((current) => current.block.id === blockId);
    if (row) {
      selection.bulkMode
        ? selection.toggle(row.key)
        : selection.setSelectedKey(row.key);
    }
  };
  const tabs = [
    { value: SEASON_TABS.PRE_SEASON, label: t('new_season_pre_season') },
    { value: SEASON_TABS.ACTIVE, label: t('new_season_active') },
    { value: SEASON_TABS.ARCHIVE, label: t('new_season_archive') },
  ];
  return (
    <div className='ns-editor'>
      <div className='ns-left-card'>
        <div className='ns-card-title'>{t('new_season_manage_seasons')}</div>
        <EntitySelects
          distributions={distributions}
          distributionId={distributionId}
          farmingUnitId={farmingUnitId}
          onNavigate={onNavigate}
        />
        <SegmentedTabs options={tabs} value={tab} onChange={setTab} />
        <label className='ns-toggle'>
          <input
            type='checkbox'
            checked={selection.bulkMode}
            onChange={() => selection.setBulkMode((on) => !on)}
          />
          <span>
            {selection.bulkMode
              ? t('new_season_bulk_edit')
              : t('new_season_bulk_select')}
          </span>
        </label>
        <div className='ns-list-scroll'>
          <SeasonList
            tab={tab}
            groups={groups}
            selectedKey={selection.selectedKey}
            bulkMode={selection.bulkMode}
            checkedKeys={selection.checkedKeys}
            onSelect={selection.setSelectedKey}
            onToggle={selection.toggle}
            onToggleAll={selection.toggleAll}
          />
        </div>
        {tab === SEASON_TABS.ARCHIVE && (
          <button
            type='button'
            className='ns-btn ns-btn-outline ns-btn-block'
            onClick={() =>
              onNavigate(farmingUnitEditorUrl(distributionId, farmingUnitId))
            }
          >
            {t('new_season_edit_fields_and_blocks')}
          </button>
        )}
      </div>
      <div className='ns-right-column'>
        <SeasonFormPanel
          tab={tab}
          activeRows={selection.activeRows}
          onActivate={
            canActivate
              ? () => activation.request(singleRow, isDirty)
              : undefined
          }
          form={form}
          errors={validation.errors}
          mapBlocks={mapBlocks}
          onMapBlockClick={onMapBlockClick}
          isSaving={isSaving}
          onSave={save}
          seasonDetails={seasonDetails}
          sensorsProps={sensorsProps}
          fitKey={distributionId + '/' + farmingUnitId}
          isModerator={tree.role === 'moderator'}
          advance={advance}
          advanceField={advanceField}
          onAdvanceEditingChange={setIsAdvanceEditing}
        />
      </div>
      <ActivateModal activation={activation} tab={tab} />
      <Toast toast={toast} />
    </div>
  );
};

module.exports = SeasonEditor;
